import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { comboBoxOptionsOf } from '@/components/ui/multi-select-utils';
import { coachesTypeaheadSearch, userTypeaheadSearch } from '@/lib/api-call-hooks';
import { ReassignmentRequestFormValues, specialRequirementsMaxLength } from '@/lib/student-group-reassignment-utils';
import { cn } from '@/lib/tailwind-utils';
import { studentGroupReassignmentReasonOptions } from '@airreading/common/data';
import { useQueryClient } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';

export interface ReassignmentRequestFormProps {
  form: UseFormReturn<ReassignmentRequestFormValues>;
  countAsReassignedLabel?: string | null;
  className?: string;
}

export const ReassignmentRequestForm = ({ form, countAsReassignedLabel, className }: ReassignmentRequestFormProps) => {
  const queryClient = useQueryClient();
  const specialRequirementsLength = form.watch('specialRequirements')?.length ?? 0;
  const isOverSpecialRequirementsLimit = specialRequirementsLength > specialRequirementsMaxLength;

  return (
    <div className={cn('grid grid-cols-2 gap-x-3 gap-y-3 pt-4', className)}>
      <div className="col-span-2 grid grid-cols-2 gap-x-3 items-start">
        <FormInputDate
          form={form}
          name="effectiveDate"
          label="Effective date"
          required
          className="[&>label]:mb-0"
          triggerButtonProps={{ className: 'h-10 w-full' }}
        />
        <FormComboBox
          form={form}
          name="requester"
          label="Requester"
          placeholder="Select an option"
          required
          className="!mt-0 [&>label]:!mb-0"
          triggerProps={{ className: 'w-full' }}
          {...comboBoxOptionsOf(userTypeaheadSearch(queryClient))}
        />
      </div>
      <div className="col-span-2">
        <FormMultiSelect
          form={form}
          name="reassignmentReasons"
          label="Reassignment reason"
          required
          multipleSelectorProps={{
            options: studentGroupReassignmentReasonOptions,
            placeholder: 'Select reason',
            className: 'w-full',
          }}
        />
      </div>
      <div className="col-span-2">
        <FormComboBox
          form={form}
          name="preferredNewTeacher"
          label="Preferred new teacher"
          placeholder="Select an option"
          className="!mt-0 [&>label]:!mb-0"
          triggerProps={{ className: 'w-full' }}
          {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient))}
        />
      </div>
      <div className="col-span-2">
        <FormTextArea
          form={form}
          name="specialRequirements"
          label="Special requirements"
          labelEndContent={
            <span className={cn('text-sm text-muted-foreground', isOverSpecialRequirementsLimit && 'text-destructive')}>
              {specialRequirementsLength} / {specialRequirementsMaxLength}
            </span>
          }
          textAreaProps={{ className: 'min-h-[72px] resize-none' }}
        />
      </div>
      {countAsReassignedLabel && (
        <div className="col-span-2 pb-4">
          <FormCheckbox
            form={form}
            name="countAsReassignedFromTestTeacher"
            label={countAsReassignedLabel}
            horizontal
            labelClassName="text-sm font-normal leading-snug"
          />
        </div>
      )}
    </div>
  );
};
